<template>
  <div class="dg-input" :class="{ icon, invert }">
    <div class="error" v-if="error && error !== ''">
      <el-tooltip v-if="compactError" class="item" effect="dark" :content="firstError" placement="top-start">
        <i class="el-icon-warning" />
      </el-tooltip>
      {{ !compactError ? firstError : '' }}
    </div>
    <input :type="type" :min="min" :max="max" v-model="val" @input="onChange" @keydown="validateInput" ref="xi" :style="hideBorderStyle" @focus="setFocus" @blur="setBlur" :disabled="isDisabled">
    <span :class="{ 'active': focus || value }" @click="focusInput" :style="hideBorderStyle">{{ placeholder }}</span>
    <fa v-if="icon" :icon="icon" />
  </div>
</template>

<script>
export default {
  name: 'DGInput',
  props: {
    compactError: Boolean,
    error: [String, Object, Array],
    validate: {
      type: [Boolean, String],
      required: false
    },
    icon: {
      type: String,
      required: false
    },
    focusFormat: {
      type: Function,
      required: false
    },
    blurFormat: {
      type: Function,
      required: false
    },
    numbersOnly: {
      type: Boolean,
      required: false
    },
    hideBorder: {
      type: String,
      required: false
    },
    type: {
      type: String,
      required: true
    },
    value: {
      type: [String, Number],
      required: false
    },
    placeholder: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      required: false
    },
    min: {
      type: String,
      required: false
    },
    max: {
      type: String,
      required: false
    },
    invert: {
      type: Boolean,
      required: false
    }
  },
  data () {
    return {
      validated: false,
      focus: false,
      val: this.value || ''
    }
  },
  watch: {
    'value' () {
      if (this.value.length > 0 || this.value === 0) {
        this.focus = true
      }
      this.val = this.value
    }
  },
  computed: {
    firstError () {
      if (typeof this.error === 'object') {
        return this.error[0]
      }

      return this.error
    },
    isDisabled () {
      if (this.disabled) {
        return true
      }

      return false
    },
    hideBorderStyle () {
      if (this.hideBorder) {
        const pos = this.hideBorder.split(' ')
        let style = {}
        pos.forEach(p => {
          switch (p) {
            case 'top':
              style.borderTop = 'none'
              break
            case 'right':
              style.borderRight = 'none'
              break
            case 'bottom':
              style.borderBottom = 'none'
              break
            case 'left':
              style.borderLeft = 'none'
              break
          }
        })
        return style
      }

      return {}
    }
  },
  methods: {
    validateInput (input) {
      if (this.numbersOnly) {
        let reg = /[0-9]|\./
        if (input.key.match(reg) === null && input.key.length === 1) {
          input.preventDefault()
        }
      }
    },
    bulkValidate () {
      if (this.validate) {
        if (this.type === 'email') {
          const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          this.validated = re.test(this.val)
        }

        if (this.type === 'text') {
          this.validated = this.val.length > 0
        }

        this.$emit('validate', this.validated)
      }
    },
    onChange () {
      this.$emit('input', this.val)
      this.bulkValidate()
    },
    setFocus () {
      if (this.focusFormat && this.val.length !== 0) {
        this.val = this.focusFormat(this.val)
      }
      this.$emit('focused', true)
      if (this.focus) {
        return
      }

      this.focus = true
    },
    focusInput () {
      // if (this.focus) {
      //   return
      // }
      this.$refs.xi.focus()
    },
    setBlur () {
      if (this.blurFormat && this.val.length !== 0) {
        this.val = this.blurFormat(this.val)
      }
      if (this.$refs.xi.value.length > 0) {
        return
      }

      this.focus = false
    }
  },
  created () {
    if (this.val.length > 0 || this.val === 0) {
      this.focus = true
      this.bulkValidate()
    }
  }
}
</script>

<style lang="scss" scoped>
  .error {
    color: $red;
    position: absolute;
    right: 0px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;

    > i {
      padding-right: 20px;
      font-size: 14px;
    }
  }

  .dg-input {
    position: relative;
    width: 100%;
    transition: border-color .2s linear;

    &:hover {
      input {
        border-color: #f2f2f2;
      }
    }

    &.icon {
      input {
        padding-left: 30px !important;
      }

      span {
        left: 30px !important;
      }
    }

    svg {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      font-size: 14px;
      left: 0px;
      color: #C0C4CC;
    }

    input {
      height: 50px;
      width: 100%;
      box-sizing: border-box;
      //box-shadow: 1px 2px 8px 0px rgba($text-light, 0.1);
      border: none;
      color: $main;
      border-bottom: 1px solid $bg;
      padding: 0px 0px;
      padding-top: 14px;
      font-size: 13px;

      &:focus {
        outline: none;
      }
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        /* display: none; <- Crashes Chrome on hover */
        -webkit-appearance: none;
        margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
    }

    span {
      // pointer-events: none;
      position: absolute;
      top: 0px;
      left: 0px;
      font-size: 13px;
      line-height: 50px;
      transform-origin: 0px 12px;
      transition-property: color, transform;
      transition-duration: 0.3s;
      transition-timing-function: cubic-bezier(0.165, 0.84, 0.44, 1);
      color: #C0C4CC;
      cursor: text;

      &.active {
        transform: scale(0.8) translateY(-10px);
        //transform-origin: 0px 20px;
        color: $text;
      }
    }

    &.invert {
      input {
        background-color: transparent !important;
        border-color: #f2f2f2 !important;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/shared/DGInput.vue